import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useSettings } from '../../../context/SettingsContext';
import { useTheme } from '../../../context/ThemeContext';
import {
  LayoutDashboard, Wallet, TrendingUp, Coins, Briefcase,
  FileText, Bell, LogOut, ChevronDown, User,
  Menu, X, CreditCard, Banknote, ArrowRightLeft, MessageSquare
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildNavItems(ct) {
  const v = (key) => ct[key] !== false;
  const items = [];

  // ACCUEIL — identique V1
  const accueilChildren = [];
  if (v('menu_dashboard')) accueilChildren.push({ label: 'Tableau de bord', href: '/client/dashboard' });
  if (v('menu_news')) accueilChildren.push({ label: "Actualit\u00e9s", href: '/client/news' });
  if (accueilChildren.length === 1) items.push(accueilChildren[0]);
  else if (accueilChildren.length > 1) items.push({ label: 'Accueil', children: accueilChildren });

  // INVESTISSEMENTS — identique V1
  const investChildren = [];
  if (v('menu_investments')) investChildren.push({ label: 'Placement', href: '/client/placements' });
  if (v('menu_my_investments')) investChildren.push({ label: 'Mes Investissements', href: '/client/my-investments' });
  investChildren.push({ label: 'Comparer', href: '/client/compare' });
  if (investChildren.length === 1) items.push(investChildren[0]);
  else if (investChildren.length > 1) items.push({ label: 'Investissements', children: investChildren });

  // SIMULATION — identique V1
  const simChildren = [];
  if (v('menu_simulator')) simChildren.push({ label: 'Simulateur de gains', href: '/client/simulator' });
  if (v('menu_simulation')) simChildren.push({ label: "Pr\u00e9visualisation", href: '/client/products' });
  if (simChildren.length === 1) items.push(simChildren[0]);
  else if (simChildren.length > 1) items.push({ label: 'Simulation', children: simChildren });

  // ARBITRAGE — identique V1
  if (v('menu_trade')) {
    items.push({ label: "Salle des march\u00e9s", href: '/client/arbitrage' });
  }

  // BOURSE — identique V1
  const bourseChildren = [];
  if (v('menu_stock')) {
    if (v('submenu_bourse_salle')) bourseChildren.push({ label: "Salle des march\u00e9s", href: '/client/bourse' });
    if (v('submenu_bourse_epargnes')) bourseChildren.push({ label: "Les \u00e9pargnes", href: '/client/portfolios' });
    if (v('submenu_bourse_portefeuille')) bourseChildren.push({ label: 'Mon portefeuille', href: '/client/portfolios' });
    if (v('submenu_bourse_actions')) bourseChildren.push({ label: 'Mes Actions', href: '/client/stock-portfolio' });
  }
  if (bourseChildren.length === 1) items.push(bourseChildren[0]);
  else if (bourseChildren.length > 1) items.push({ label: 'Bourse', children: bourseChildren });

  // CRYPTO — identique V1
  const cryptoChildren = [];
  if (v('menu_crypto')) {
    if (v('submenu_crypto_salle')) cryptoChildren.push({ label: 'Acheter des cryptos', href: '/client/crypto-buy' });
    if (v('submenu_crypto_portfolio') || !('submenu_crypto_portfolio' in ct)) cryptoChildren.push({ label: 'Mes Cryptos', href: '/client/crypto-portfolio' });
    if (v('submenu_crypto_epargnes')) cryptoChildren.push({ label: "Les \u00e9pargnes", href: '/client/portfolios' });
  }
  if (v('menu_wallets')) cryptoChildren.push({ label: 'Mon wallet', href: '/client/wallets' });
  if (cryptoChildren.length === 1) items.push(cryptoChildren[0]);
  else if (cryptoChildren.length > 1) items.push({ label: 'Crypto', children: cryptoChildren });

  // MON ESPACE — identique V1
  const espaceChildren = [];
  if (v('menu_transactions')) espaceChildren.push({ label: 'Historique', href: '/client/transactions' });
  if (v('menu_messages')) espaceChildren.push({ label: 'Messagerie', href: '/messages' });
  if (v('menu_documents')) espaceChildren.push({ label: 'Mes documents', href: '/client/documents' });
  if (v('menu_profile')) espaceChildren.push({ label: 'Profil', href: '/client/profile' });
  if (espaceChildren.length === 1) items.push(espaceChildren[0]);
  else if (espaceChildren.length > 1) items.push({ label: 'Mon Espace', children: espaceChildren });

  return items;
}

function useThemeColors() {
  const { currentTemplate, branding } = useTheme();
  return { ...(currentTemplate?.colors || {}), ...(branding?.customColors || {}) };
}

/* ─── Dropdown nav item ─── */
function NavDropdown({ item, primary, fg, mutedFg, cardBg, borderColor }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const location = useLocation();
  const isChildActive = item.children?.some(c => location.pathname === c.href);

  const handleEnter = () => { clearTimeout(timeoutRef.current); setOpen(true); };
  const handleLeave = () => { timeoutRef.current = setTimeout(() => setOpen(false), 150); };

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        className="flex flex-col items-center justify-center h-full px-4 text-[13px] font-medium transition-colors duration-150"
        style={{ color: isChildActive ? primary : fg }}
      >
        <span>{item.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      <div
        className="absolute top-full left-1/2 -translate-x-1/2 pt-1 z-50"
        style={{ opacity: open ? 1 : 0, visibility: open ? 'visible' : 'hidden', transition: 'opacity 150ms, visibility 150ms', pointerEvents: open ? 'auto' : 'none' }}
      >
        <div className="py-1.5 rounded-lg shadow-xl min-w-[200px] border"
          style={{ background: cardBg, borderColor: borderColor }}>
          {item.children.map(child => {
            const active = location.pathname === child.href;
            return (
              <Link key={child.href} to={child.href}
                className="block px-4 py-2.5 text-[13px] transition-colors duration-100"
                style={{
                  color: active ? primary : fg,
                  fontWeight: active ? 600 : 400,
                  background: active ? primary + '08' : 'transparent',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = `${borderColor}80`; }}
                onMouseLeave={e => { e.currentTarget.style.background = active ? primary + '08' : 'transparent'; }}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Simple nav link ─── */
function NavItem({ item, primary, fg }) {
  const location = useLocation();
  const isActive = location.pathname === item.href || (item.href === '/client/dashboard' && location.pathname === '/client');

  return (
    <Link to={item.href}
      className="flex items-center h-full px-4 text-[13px] font-medium transition-colors duration-150"
      style={{
        color: isActive ? primary : fg,
        borderBottom: isActive ? `2px solid ${primary}` : '2px solid transparent',
      }}
    >
      {item.label}
    </Link>
  );
}

/* ─── User Dropdown ─── */
function UserDropdown({ user, primary, fg, mutedFg, cardBg, borderColor, errorColor, successColor, warningColor, accentColor, logout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const initials = user ? `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() : '?';

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const t = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', handler); };
  }, [open]);

  const go = (e, href) => {
    e.stopPropagation();
    setOpen(false);
    setTimeout(() => navigate(href), 10);
  };

  const handleLogout = (e) => {
    e.stopPropagation();
    setOpen(false);
    setTimeout(() => logout(), 10);
  };

  const MenuItem = ({ label, icon: Icon, color, href }) => (
    <button
      onMouseDown={(e) => go(e, href)}
      className="flex items-center gap-3 px-4 py-2.5 text-[12px] w-full text-left transition-colors cursor-pointer"
      style={{ color }}
      onMouseEnter={e => e.currentTarget.style.background = `${borderColor}60`}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 py-1 px-1.5 rounded-full transition-colors"
        style={{ background: open ? `${borderColor}60` : 'transparent' }}
        data-testid="v3-user-menu">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold"
          style={{ background: primary + '15', color: primary }}>
          {initials}
        </div>
        <ChevronDown className="w-3 h-3" style={{ color: mutedFg, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 py-1 rounded-xl shadow-2xl min-w-[230px] border z-[60]"
          style={{ background: cardBg, borderColor: borderColor }}
          onClick={e => e.stopPropagation()}>

          <div className="px-4 py-3 border-b" style={{ borderColor: `${borderColor}80` }}>
            <p className="text-[13px] font-semibold">{user?.first_name} {user?.last_name}</p>
            <p className="text-[11px] mt-0.5" style={{ color: mutedFg }}>{user?.email}</p>
          </div>

          <div className="py-1 border-b" style={{ borderColor: `${borderColor}80` }}>
            <MenuItem label="Ajout de fonds" icon={CreditCard} color={successColor} href="/client/transactions?action=deposit" />
            <MenuItem label="Retrait" icon={Banknote} color={warningColor} href="/client/transactions?action=withdraw" />
            <MenuItem label="Transfert interne" icon={ArrowRightLeft} color={accentColor || '#60A5FA'} href="/client/dashboard?action=transfer" />
          </div>

          <div className="py-1 border-b" style={{ borderColor: `${borderColor}80` }}>
            <MenuItem label="Mon profil" icon={User} color={fg} href="/client/profile" />
            <MenuItem label="Mes documents" icon={FileText} color={fg} href="/client/documents" />
          </div>

          <div className="py-1">
            <button
              onMouseDown={handleLogout}
              className="flex items-center gap-3 px-4 py-2.5 text-[12px] w-full text-left transition-colors cursor-pointer"
              style={{ color: errorColor }}
              onMouseEnter={e => e.currentTarget.style.background = `${borderColor}60`}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> {"D\u00e9connexion"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardLayoutV3({ children }) {
  const { user, logout, mirrorMode } = useAuth();
  const { platformSettings, clientTemplate } = useSettings();
  const { branding } = useTheme();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const token = localStorage.getItem('token');

  const NAV_ITEMS = useMemo(() => buildNavItems(clientTemplate || {}), [clientTemplate]);

  useEffect(() => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/messages/unread-count`, { headers: h }).then(r => setUnread(r.data?.count || 0)).catch(() => {});
    axios.get(`${API}/notifications/unread-count`, { headers: h }).then(r => setNotifCount(r.data?.count || 0)).catch(() => {});
  }, [token]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const platformName = platformSettings?.platform_name || branding?.platformName || 'Plateforme';
  const logoUrl = branding?.logoUrl || platformSettings?.logo_url;
  const logoSize = branding?.logoSize || platformSettings?.logo_size || 40;
  const showName = branding?.showPlatformName !== false && platformSettings?.show_platform_name !== false;
  const headerHeight = Math.max(48, logoSize + 16);
  const primary = colors.primary || '#FFFFFF';
  const fg = colors.foreground || '#FAFAFA';
  const mutedFg = colors.mutedForeground || '#8A8A8A';
  const cardBg = colors.card || '#111111';
  const borderColor = colors.border || '#222222';
  const bg = colors.background || '#030303';

  return (
    <div className="min-h-screen" style={{ background: bg, color: fg }}>
      {/* Mirror mode banner */}
      {mirrorMode && (
        <div className="bg-amber-500 text-black text-center py-1 px-4 text-xs font-medium z-[60] relative" data-testid="mirror-mode-banner">
          Mode miroir — Vous visualisez l'espace de {user?.first_name} {user?.last_name}
          <button onClick={() => window.location.href = '/admin/clients'} className="ml-3 underline font-bold">Quitter</button>
        </div>
      )}
      {/* TOP BAR */}
      <header className="sticky top-0 z-50" style={{ background: cardBg, borderBottom: `1px solid ${borderColor}` }}>
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 flex items-center justify-between" style={{ height: `${headerHeight}px` }}>

          {/* LEFT: Logo */}
          <div className="flex items-center gap-6 h-full">
            <Link to="/client/dashboard" className="flex items-center gap-2 shrink-0" data-testid="v3-logo">
              {logoUrl ? (
                <img src={logoUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : logoUrl}
                  alt="" style={{ height: `${logoSize}px`, width: 'auto' }} className="object-contain" />
              ) : (
                <div className="rounded flex items-center justify-center font-bold"
                  style={{ width: `${logoSize}px`, height: `${logoSize}px`, fontSize: `${Math.max(10, logoSize / 3)}px`, background: primary, color: colors.primaryForeground || '#000' }}>
                  {platformName[0]}
                </div>
              )}
              {showName && <span className="text-[13px] font-semibold hidden sm:inline">{platformName}</span>}
            </Link>

            {/* DESKTOP NAV */}
            <nav className="hidden lg:flex items-center h-full gap-0" data-testid="v3-nav-desktop">
              {NAV_ITEMS.map(item =>
                item.children
                  ? <NavDropdown key={item.label} item={item} primary={primary} fg={fg} mutedFg={mutedFg} cardBg={cardBg} borderColor={borderColor} />
                  : <NavItem key={item.label} item={item} primary={primary} fg={fg} />
              )}
            </nav>
          </div>

          {/* RIGHT: Icons + User */}
          <div className="flex items-center gap-0.5">
            <button onClick={() => navigate('/notifications')}
              className="relative p-2.5 rounded-full transition-colors hover:bg-white/5"
              data-testid="v3-notif-btn">
              <Bell className="w-[17px] h-[17px]" style={{ color: mutedFg }} strokeWidth={1.8} />
              {notifCount > 0 && (
                <span className="absolute top-1 right-1 w-[14px] h-[14px] rounded-full text-[8px] font-bold flex items-center justify-center"
                  style={{ background: colors.error || '#EF4444', color: '#fff' }}>{notifCount > 9 ? '9+' : notifCount}</span>
              )}
            </button>

            <button onClick={() => navigate('/messages')}
              className="relative p-2.5 rounded-full transition-colors hover:bg-white/5"
              data-testid="v3-msg-btn">
              <MessageSquare className="w-[17px] h-[17px]" style={{ color: mutedFg }} strokeWidth={1.8} />
              {unread > 0 && (
                <span className="absolute top-1 right-1 w-[14px] h-[14px] rounded-full text-[8px] font-bold flex items-center justify-center"
                  style={{ background: primary, color: colors.primaryForeground || '#000' }}>{unread > 9 ? '9+' : unread}</span>
              )}
            </button>

            <div className="w-px h-5 mx-2 hidden sm:block" style={{ background: borderColor }} />

            <UserDropdown
              user={user} primary={primary} fg={fg} mutedFg={mutedFg}
              cardBg={cardBg} borderColor={borderColor}
              errorColor={colors.error || '#EF4444'}
              successColor={colors.success || '#4ADE80'}
              warningColor={colors.warning || '#FBBF24'}
              accentColor={colors.accent || '#60A5FA'}
              logout={logout}
            />

            <button className="lg:hidden p-2 rounded-lg ml-1"
              style={{ color: mutedFg }}
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="v3-mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* MOBILE NAV */}
        {mobileOpen && (
          <div className="lg:hidden border-t" style={{ borderColor, background: cardBg }}>
            <div className="max-w-[1600px] mx-auto px-4 py-3 space-y-0.5">
              {NAV_ITEMS.map(item =>
                item.children ? (
                  <div key={item.label} className="py-1">
                    <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: mutedFg }}>{item.label}</p>
                    {item.children.map(child => (
                      <Link key={child.href} to={child.href}
                        className="block px-6 py-2 text-[13px] rounded-lg transition-colors"
                        style={{ color: location.pathname === child.href ? primary : fg }}>
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link key={item.href} to={item.href}
                    className="block px-3 py-2 text-[13px] rounded-lg transition-colors"
                    style={{ color: location.pathname === item.href ? primary : fg, fontWeight: location.pathname === item.href ? 600 : 400 }}>
                    {item.label}
                  </Link>
                )
              )}
              <div className="border-t pt-2 mt-2" style={{ borderColor }}>
                <Link to="/client/documents" className="flex items-center gap-2 px-3 py-2 text-[13px] rounded-lg" style={{ color: fg }}>
                  <FileText className="w-4 h-4" /> Mes documents
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ═══ CONTENT ═══ */}
      <main className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        {children}
      </main>
    </div>
  );
}
